import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { childrenAPI } from '../services/api';

// Fix default Leaflet icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const redIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
});

const orangeIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-orange.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
});

function FlyTo({ center, zoom = 13 }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom, { duration: 0.8 });
  }, [center, zoom, map]);
  return null;
}

function ageText(value) {
  if (!value) return 'Derniere lecture inconnue';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Derniere lecture inconnue';
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return 'Il y a moins d une minute';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `Il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  return `Il y a ${days} j`;
}

function validCoord(lat, lng) {
  return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
}

function formatPlace(child) {
  const loc = child.lastLocation || {};
  return loc.placeLabel || loc.address || loc.displayName || child.address || 'Position disponible';
}

function initials(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase() || 'TK';
}

function normalizeChild(child) {
  const loc = child.lastLocation || {};
  const lat = Number(loc.latitude ?? child.latitude);
  const lng = Number(loc.longitude ?? child.longitude);
  const batteryRaw = child.batteryLevel ?? child.battery ?? loc.batteryLevel ?? null;
  const batteryNumber = batteryRaw === null || batteryRaw === undefined ? null : Number(batteryRaw);
  const battery = Number.isFinite(batteryNumber) ? Math.round(batteryNumber) : null;
  const online = Boolean(child.isOnline || child.online);
  const status = online ? 'online' : (battery !== null && battery < 20 ? 'lowbatt' : 'offline');

  return {
    ...child,
    lat,
    lng,
    battery,
    status,
    addr: formatPlace(child),
    parentName: child.parentName || child.parent?.name || '-',
    phone: child.parentPhone || child.phone || child.parent?.phone || '-',
    age: child.age || '-',
    lastSeen: ageText(child.lastSeenAt || loc.updatedAt || child.updatedAt || child.createdAt),
  };
}

export default function LocationPage() {
  const [query, setQuery] = useState('');
  const [children, setChildren] = useState([]);
  const [selected, setSelected] = useState(null);
  const [flyTo, setFlyTo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadChildren = async (search = '') => {
    setLoading(true);
    setError('');
    try {
      const res = await childrenAPI.getChildren(1, 80, search);
      const rows = (res.data?.children || [])
        .map(normalizeChild)
        .filter(child => validCoord(child.lat, child.lng));

      setChildren(rows);
      setSelected(current => current ? rows.find(child => child.id === current.id) || null : null);
      if (rows.length > 0) setFlyTo([rows[0].lat, rows[0].lng]);
    } catch (err) {
      setChildren([]);
      setSelected(null);
      setError(err.response?.data?.error || err.message || 'Lecture localisation impossible.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadChildren('');
  }, []);

  const search = () => loadChildren(query.trim());

  const focusChild = (child) => {
    setSelected(child);
    setFlyTo([child.lat, child.lng]);
  };

  const mapCenter = flyTo || [7.3697, 12.3547];
  const mapZoom = flyTo ? 13 : 6;

  return (
    <div style={S.page}>
      <div style={S.searchRow}>
        <input
          style={S.input}
          type="search"
          placeholder="Nom, telephone parent ou enfant..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && search()}
        />
        <button style={S.searchBtn} onClick={search} disabled={loading}>
          {loading ? 'Lecture...' : 'Rechercher'}
        </button>
        <button style={S.secondaryBtn} onClick={() => loadChildren(query.trim())} disabled={loading}>
          Rafraichir
        </button>
      </div>

      {error && <div style={S.error}>{error}</div>}

      <div style={S.layout}>
        <div style={S.mapWrap}>
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            style={{ height: '100%', width: '100%', borderRadius: 8 }}
          >
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {flyTo && <FlyTo center={flyTo} />}

            {children.map(child => (
              <Marker
                key={child.id}
                position={[child.lat, child.lng]}
                icon={child.status === 'lowbatt' ? orangeIcon : redIcon}
                eventHandlers={{ click: () => setSelected(child) }}
              >
                <Popup>
                  <div style={{ fontFamily: 'monospace', fontSize: 13, background: '#10224a', color: '#ffffff', padding: 4, minWidth: 190 }}>
                    <strong style={{ color: '#f5a623' }}>{child.name}</strong><br />
                    Parent: {child.parentName}<br />
                    Tel: {child.phone}<br />
                    Adresse: {child.addr}<br />
                    Batterie: <span style={{ color: child.battery !== null && child.battery < 20 ? '#e63946' : '#22c55e' }}>{child.battery ?? 'N/A'}%</span><br />
                    <span style={{ color: 'rgba(255,255,255,.58)', fontSize: 11 }}>{child.lastSeen}</span>
                  </div>
                </Popup>
                <Circle
                  center={[child.lat, child.lng]}
                  radius={500}
                  pathOptions={{ color: child.status === 'lowbatt' ? '#f5a623' : '#22c55e', fillOpacity: 0.05, weight: 1 }}
                />
              </Marker>
            ))}
          </MapContainer>
        </div>

        <div style={S.side}>
          {selected ? (
            <div>
              <div style={S.panelHeader}>
                <span style={S.panelTitle}>{selected.name}</span>
                <button style={S.closeBtn} onClick={() => setSelected(null)}>x</button>
              </div>
              <div style={S.fieldList}>
                {[
                  ['Parent', selected.parentName],
                  ['Telephone', selected.phone],
                  ['Age', selected.age === '-' ? '-' : `${selected.age} ans`],
                  ['Adresse', selected.addr],
                  ['Latitude', selected.lat.toFixed(6)],
                  ['Longitude', selected.lng.toFixed(6)],
                  ['Batterie', selected.battery === null ? 'N/A' : `${selected.battery}%`],
                  ['Statut', selected.status],
                  ['Vu', selected.lastSeen],
                ].map(([label, value]) => (
                  <div key={label} style={S.field}>
                    <div style={S.fieldLabel}>{label}</div>
                    <div style={S.fieldVal}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <div style={S.panelHeader}>
                <span style={S.panelTitle}>{children.length} position(s) DB</span>
              </div>
              {loading && <div style={S.empty}>Chargement des positions...</div>}
              {!loading && children.length === 0 && (
                <div style={S.empty}>Aucune position DB disponible.</div>
              )}
              {!loading && children.map(child => (
                <div key={child.id} style={S.childRow} onClick={() => focusChild(child)}>
                  <div style={S.childAvatar}>{initials(child.name)}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={S.childName}>{child.name}</div>
                    <div style={S.childMeta}>{child.addr}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 11, color: child.battery !== null && child.battery < 20 ? '#e63946' : 'rgba(255,255,255,.72)' }}>
                      {child.battery === null ? 'N/A' : `${child.battery}%`}
                    </div>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,.58)' }}>{child.lastSeen}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const S = {
  page: { padding: 20, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 54px)', boxSizing: 'border-box' },
  searchRow: { display: 'flex', gap: 10, marginBottom: 16 },
  input: { flex: 1, background: '#10224a', border: '1px solid rgba(255,255,255,.14)', color: '#ffffff', fontFamily: 'monospace', fontSize: 13, padding: '9px 14px', borderRadius: 6, outline: 'none' },
  searchBtn: { background: '#f5a623', color: '#06162f', border: 'none', fontFamily: 'monospace', fontSize: 12, fontWeight: 800, padding: '9px 16px', borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap' },
  secondaryBtn: { background: '#1e4db7', color: '#fff', border: 'none', fontFamily: 'monospace', fontSize: 12, fontWeight: 700, padding: '9px 16px', borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap' },
  error: { background: 'rgba(230,57,70,.16)', border: '1px solid rgba(230,57,70,.35)', color: '#ffb4b4', borderRadius: 6, padding: '9px 12px', marginBottom: 12, fontFamily: 'monospace', fontSize: 12 },
  layout: { display: 'flex', gap: 16, flex: 1, minHeight: 0 },
  mapWrap: { flex: 1, borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(255,255,255,.14)', minWidth: 0 },
  side: { width: 320, background: '#10224a', border: '1px solid rgba(255,255,255,.14)', borderRadius: 8, overflow: 'hidden auto', flexShrink: 0 },
  panelHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid rgba(255,255,255,.14)' },
  panelTitle: { fontSize: 12, fontWeight: 700, color: '#ffffff' },
  closeBtn: { background: 'none', border: 'none', color: 'rgba(255,255,255,.58)', cursor: 'pointer', fontSize: 14 },
  fieldList: { padding: '8px 0' },
  field: { padding: '7px 14px', borderBottom: '1px solid rgba(48,54,61,.4)' },
  fieldLabel: { fontSize: 10, color: 'rgba(255,255,255,.58)', letterSpacing: .5, textTransform: 'uppercase', marginBottom: 2 },
  fieldVal: { fontSize: 12, color: '#ffffff', overflowWrap: 'anywhere' },
  childRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderBottom: '1px solid rgba(48,54,61,.4)', cursor: 'pointer' },
  childAvatar: { width: 30, height: 30, borderRadius: '50%', background: '#1e4db7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#fff', fontWeight: 700, flexShrink: 0 },
  childName: { fontSize: 12, color: '#ffffff', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  childMeta: { fontSize: 10, color: 'rgba(255,255,255,.72)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  empty: { padding: 16, color: 'rgba(255,255,255,.72)', fontFamily: 'monospace', fontSize: 12 },
};
